<template>
  <div class="collapsible-filter-card" :class="{ 'is-expanded': isExpanded }">
    <div class="filter-header" @click="toggleExpand">
      <div class="filter-header-left">
        <feather-icon icon="FilterIcon" class="filter-icon" />
        <span class="filter-title">Filtros</span>
        <span v-if="activeFiltersCount > 0" class="filter-badge">
          {{ activeFiltersCount }} {{ activeFiltersCount === 1 ? 'ativo' : 'ativos' }}
        </span>
      </div>
      <div class="filter-header-right">
        <button
          v-if="activeFiltersCount > 0"
          class="btn-clear-inline"
          @click.stop="$emit('clear')"
        >
          <feather-icon icon="XIcon" size="14" />
          Limpar
        </button>
        <feather-icon
          :icon="isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'"
          class="expand-icon"
        />
      </div>
    </div>

    <transition name="slide-fade">
      <div v-show="isExpanded" class="filter-body">
        <div class="filter-grid">
          <slot></slot>
        </div>
        <div class="filter-actions">
          <button class="btn-filter-primary" @click="$emit('search')">
            <feather-icon icon="SearchIcon" size="16" />
            Pesquisar
          </button>
          <button class="btn-filter-secondary" @click="$emit('clear')">
            <feather-icon icon="RefreshCwIcon" size="16" />
            Limpar
          </button>
        </div>
      </div>
    </transition>

    <div v-if="!isExpanded && activeFiltersCount > 0" class="filter-summary">
      <span class="summary-text">
        <feather-icon icon="InfoIcon" size="14" />
        {{ activeFiltersCount }} {{ activeFiltersCount === 1 ? 'filtro aplicado' : 'filtros aplicados' }}
      </span>
      <button class="btn-expand-filters" @click="toggleExpand">
        Expandir para editar
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CollapsibleFilters',
  props: {
    activeFiltersCount: {
      type: Number,
      default: 0,
    },
    initialExpanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isExpanded: this.initialExpanded,
    };
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },
    expand() {
      this.isExpanded = true;
    },
    collapse() {
      this.isExpanded = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.collapsible-filter-card {
  background: var(--sf-surface-bg, #ffffff);
  border: 1px solid var(--sf-border-light, #e5e8f0);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  &.is-expanded {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

.filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1.25rem;
  cursor: pointer;
  background: var(--sf-surface-secondary, #f8f9fc);
  border-bottom: 1px solid transparent;
  transition: all 0.2s ease;

  .is-expanded & {
    border-bottom-color: var(--sf-border-light, #e5e8f0);
    background: var(--sf-surface-bg, #ffffff);
  }

  &:hover {
    background: var(--sf-surface-hover, #f0f2f8);
  }
}

.filter-header-left {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.filter-icon {
  color: var(--sf-primary, #5d54d4);
  width: 18px;
  height: 18px;
}

.filter-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--sf-text-primary, #2f2b3d);
}

.filter-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.1875rem 0.625rem;
  background: var(--sf-primary-light, rgba(93, 84, 212, 0.1));
  color: var(--sf-primary, #5d54d4);
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 100px;
}

.filter-header-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.btn-clear-inline {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--sf-text-muted, #5f6472);
  background: transparent;
  border: 1px solid var(--sf-border-light, #e5e8f0);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: var(--sf-danger, #c94243);
    border-color: var(--sf-danger, #c94243);
    background: rgba(201, 66, 67, 0.05);
  }
}

.expand-icon {
  color: var(--sf-text-muted, #5f6472);
  width: 20px;
  height: 20px;
  transition: transform 0.3s ease;
}

.filter-body {
  padding: 1.25rem;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
}

.filter-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--sf-border-light, #e5e8f0);
}

.btn-filter-primary,
.btn-filter-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-filter-primary {
  background: var(--sf-primary, #5d54d4);
  color: #ffffff;
  border: none;

  &:hover {
    background: var(--sf-primary-dark, #4a43a8);
    box-shadow: 0 4px 12px rgba(93, 84, 212, 0.3);
  }
}

.btn-filter-secondary {
  background: transparent;
  color: var(--sf-text-muted, #5f6472);
  border: 1px solid var(--sf-border-light, #e5e8f0);

  &:hover {
    background: var(--sf-surface-hover, #f0f2f8);
    border-color: var(--sf-border, #d0d4e0);
  }
}

.filter-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.25rem;
  background: var(--sf-primary-light, rgba(93, 84, 212, 0.05));
  border-top: 1px solid var(--sf-border-light, #e5e8f0);
}

.summary-text {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--sf-primary, #5d54d4);
  font-weight: 500;
}

.btn-expand-filters {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--sf-primary, #5d54d4);
  background: transparent;
  border: none;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: var(--sf-primary-dark, #4a43a8);
  }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.slide-fade-enter,
.slide-fade-leave-to {
  max-height: 0;
  opacity: 0;
  padding: 0 1.25rem;
}

body.dark-layout {
  .collapsible-filter-card {
    background: var(--sf-surface-bg, #283046);
    border-color: var(--sf-border-light, #3b4253);

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    &.is-expanded {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }
  }

  .filter-header {
    background: var(--sf-surface-secondary, #242b3d);

    &:hover {
      background: var(--sf-surface-hover, #2d3548);
    }

    .is-expanded & {
      background: var(--sf-surface-bg, #283046);
      border-bottom-color: var(--sf-border-light, #3b4253);
    }
  }

  .filter-summary {
    background: rgba(93, 84, 212, 0.1);
    border-top-color: var(--sf-border-light, #3b4253);
  }
}
</style>
